import { useLayoutEffect, useRef, useState } from 'react';

import { TILE_STRIDE, VIEWPORT_BUFFER_TILES } from '../constants';

interface ConveyorCapacity {
  viewportRef: React.RefObject<HTMLDivElement>;
  containerWidth: number;
  // Belt slots needed to fill the viewport: visible tiles + an off-screen buffer
  // so recycling at the edges stays seamless. 0 until the viewport is measured.
  minSlots: number;
}

// How many tile slots are needed to cover a viewport of the given width.
export function computeTilesPerViewport(containerWidth: number, stride: number = TILE_STRIDE): number {
  if (containerWidth <= 0 || stride <= 0) return 0;
  return Math.ceil(containerWidth / stride);
}

// Measures the conveyor viewport and derives how many tiles fit. The width is
// read synchronously in a layout effect (before paint) so minSlots is resolved
// on the first commit — otherwise the show/hide gate would race the data on
// first load and the banner wouldn't appear until a later re-render. A
// ResizeObserver keeps it in sync afterwards.
export function useConveyorCapacity(): ConveyorCapacity {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const measure = () => setContainerWidth(el.clientWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const minSlots = containerWidth > 0 ? computeTilesPerViewport(containerWidth) + VIEWPORT_BUFFER_TILES : 0;

  return { viewportRef, containerWidth, minSlots };
}
